import React, { useState, useEffect, useCallback } from 'react';
import { X, User, Mail, Send, PackageCheck, FileSearch, Edit2, Clock, ArrowRight, MessageSquare, CheckCircle2, AlertTriangle, ChevronRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { apiGet, apiPost } from '../../utils/api';
import toast from 'react-hot-toast';
import AuditAssignmentPanel from './AuditAssignmentPanel';
import AuditFileSelectionModal from './AuditFileSelectionModal';
import UnitSelectionModal from './UnitSelectionModal';
import AskJPro from './AskJPro';
import AuditManualEntryForm from './AuditManualEntryForm';

const STATUS_COLORS = {
  scheduled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  setup_requested: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  files_selected: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  files_requested: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  partially_received: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  all_received: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
  in_review: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  review_complete: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  paused: 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
  unresponsive: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const Badge = ({ status }) => {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
  const label = status?.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) || '';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${cls}`}>{label}</span>;
};

const PropertySidePanel = ({ propertyId, onClose, teamMembers = [], onPropertyUpdated }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [tenantFiles, setTenantFiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [activeSection, setActiveSection] = useState('overview');
  const [mirror, setMirror] = useState(null); // processor-mirror detail (real contacts/files)
  const [note, setNote] = useState('');
  const [showFileSelection, setShowFileSelection] = useState(false);
  const [showUnitSelection, setShowUnitSelection] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/admin/audit_tracker/properties/${propertyId}`);
      if (res.ok) {
        const data = await res.json();
        setProperty(data.property);
        setContacts(data.contacts || []);
        setTenantFiles(data.tenant_files || []);
        setEvents(data.events || []);
        // Pull the real contacts/files straight from the processor mirror — the
        // provisioned JPRO contacts are often empty even when the mirror has them.
        const ppid = data.property?.processor_property_id;
        if (ppid) {
          apiGet(`/admin/audit_tracker/portfolio/properties/${ppid}/detail`)
            .then(async (r) => { if (r.ok) setMirror(await r.json()); })
            .catch(() => {});
        } else {
          setMirror(null);
        }
      }
    } catch {}
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const sendEmail = async (type) => {
    try {
      const res = await apiPost(`/admin/audit_tracker/properties/${propertyId}/send_${type}`);
      if (res.ok) { const data = await res.json(); toast.success(data.message); fetchDetail(); onPropertyUpdated?.(); }
      else { const data = await res.json(); toast.error(data.error); }
    } catch (err) { toast.error(err.message); }
  };

  const markReceived = async () => {
    try {
      const res = await apiPost(`/admin/audit_tracker/properties/${propertyId}/mark_received`, { count: 1 });
      if (res.ok) { const data = await res.json(); toast.success(data.message); fetchDetail(); onPropertyUpdated?.(); }
      else { const data = await res.json(); toast.error(data.error); }
    } catch (err) { toast.error(err.message); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await apiPost(`/admin/audit_tracker/properties/${propertyId}/events`, { event_type: 'note_added', note: note.trim() });
      setNote('');
      fetchDetail();
    } catch {}
  };

  if (!propertyId) return null;

  const secBtn = (key, label) => (
    <button
      onClick={() => setActiveSection(key)}
      className={`px-3 py-1.5 text-[12px] font-rasa font-medium rounded-lg transition-colors ${
        activeSection === key
          ? (d ? 'bg-gray-700 text-white' : 'bg-gray-800 text-white')
          : (d ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100')
      }`}
    >
      {label}
    </button>
  );

  const ProgressBar = ({ value, max, color = 'blue' }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <div className={`flex-1 h-1.5 rounded-full ${d ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div className={`h-1.5 rounded-full bg-${color}-500 transition-all`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`text-[11px] font-rasa font-medium ${d ? 'text-gray-400' : 'text-gray-500'}`}>{value}/{max}</span>
      </div>
    );
  };

  return (
    <div className={`fixed top-0 right-0 h-full w-[440px] z-40 shadow-2xl border-l overflow-hidden flex flex-col transition-transform duration-200 ${
      d ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className={`flex items-start justify-between px-5 py-4 border-b shrink-0 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex-1 min-w-0 mr-3">
          {loading ? (
            <div className={`h-5 w-48 rounded ${d ? 'bg-gray-700' : 'bg-gray-200'} animate-pulse`} />
          ) : (
            <>
              <h3 className="text-base font-dm-serif truncate">{property?.property_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge status={property?.status} />
                {property?.management_company && (
                  <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{property.management_company}</span>
                )}
              </div>
            </>
          )}
        </div>
        <button onClick={onClose} className={`p-1.5 rounded-lg shrink-0 ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
          <X size={16} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center flex-1"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" /></div>
      ) : property && (
        <>
          {/* Quick actions strip */}
          <div className={`flex items-center gap-1.5 px-5 py-2.5 border-b shrink-0 overflow-x-auto ${d ? 'border-gray-700' : 'border-gray-200'}`}>
            {property.status === 'scheduled' && (
              <button onClick={() => sendEmail('setup')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
                <Send size={11} /> Setup Email
              </button>
            )}
            {['scheduled', 'setup_requested', 'files_selected'].includes(property.status) || property.files_required === 0 ? (
              <button onClick={() => setShowFileSelection(true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-indigo-600 text-white hover:bg-indigo-700 whitespace-nowrap">
                <FileSearch size={11} /> {property.files_required > 0 ? 'Re-run Selection' : 'File Selection'}
              </button>
            ) : null}
            {property.processor_property_id && ['scheduled', 'setup_requested', 'files_selected'].includes(property.status) && (
              <button onClick={() => setShowUnitSelection(true)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
                <FileSearch size={11} /> Select Units
              </button>
            )}
            {property.status === 'files_selected' && (
              <button onClick={() => sendEmail('file_request')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
                <Send size={11} /> File Request
              </button>
            )}
            {['files_requested', 'partially_received'].includes(property.status) && (
              <>
                <button onClick={markReceived} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-emerald-600 text-white hover:bg-emerald-700 whitespace-nowrap">
                  <PackageCheck size={11} /> Mark Received
                </button>
                <button onClick={() => sendEmail('followup')} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-rasa font-medium bg-orange-600 text-white hover:bg-orange-700 whitespace-nowrap">
                  <Send size={11} /> Follow-up
                </button>
              </>
            )}
            <button onClick={() => setShowEdit(true)} className={`p-1.5 rounded-lg ml-auto ${d ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title="Edit">
              <Edit2 size={13} />
            </button>
          </div>

          {/* Section tabs */}
          <div className={`flex items-center gap-1 px-5 py-2 border-b shrink-0 ${d ? 'border-gray-700' : 'border-gray-200'}`}>
            {secBtn('overview', 'Overview')}
            {secBtn('files', `Files (${tenantFiles.length})`)}
            {secBtn('timeline', `Timeline (${events.length})`)}
            {secBtn('jpro', 'Ask JPro')}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {activeSection === 'overview' && (
              <div className="space-y-4">
                {/* Progress */}
                <div className="space-y-2">
                  <div>
                    <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Collection</span>
                    <ProgressBar value={property.files_received} max={property.files_required} color="blue" />
                  </div>
                  <div>
                    <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Review</span>
                    <ProgressBar value={property.files_completed} max={property.files_required} color="emerald" />
                  </div>
                </div>

                {/* Assignment */}
                <div className={`p-3 rounded-lg ${d ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className={`text-[11px] font-rasa font-bold mb-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Assigned To</div>
                  <AuditAssignmentPanel property={property} teamMembers={teamMembers} onAssigned={(p) => { setProperty(p); onPropertyUpdated?.(); }} />
                </div>

                {/* Details */}
                <div className={`grid grid-cols-2 gap-3 p-3 rounded-lg ${d ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  {[
                    { l: 'State', v: property.state },
                    { l: 'Month', v: property.audit_month ? new Date(2000, property.audit_month - 1).toLocaleString('default', { month: 'long' }) : '-' },
                    { l: 'Units', v: property.qty_units },
                    { l: 'Affordable', v: property.qty_affordable_units },
                    { l: 'Due Date', v: property.due_date || '-' },
                    { l: 'Start Date', v: property.start_date || '-' },
                  ].map((i) => (
                    <div key={i.l}>
                      <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{i.l}</div>
                      <div className={`text-[13px] font-rasa ${d ? 'text-gray-200' : 'text-gray-700'}`}>{i.v || '-'}</div>
                    </div>
                  ))}
                </div>

                {/* Contacts */}
                {contacts.length > 0 && (
                  <div>
                    <div className={`text-[11px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Contacts</div>
                    {contacts.map((c) => (
                      <div key={c.id} className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                        <User size={13} className={d ? 'text-gray-500' : 'text-gray-400'} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12px] font-rasa truncate ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                            {c.name || c.email} {c.primary_contact && <span className="text-blue-500 text-[10px]">(Primary)</span>}
                          </div>
                          {c.name && c.email && <div className={`text-[11px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>{c.email}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Real contacts from the processor mirror (shown when no JPRO
                    contact has been provisioned — the common case). */}
                {contacts.length === 0 && mirror?.contacts?.length > 0 && (
                  <div>
                    <div className={`text-[11px] font-rasa font-bold mb-2 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
                      Contacts <span className="font-normal opacity-70">(from processor)</span>
                    </div>
                    {mirror.contacts.map((c, i) => (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded-lg mb-1 ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                        <User size={13} className={d ? 'text-gray-500' : 'text-gray-400'} />
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12px] font-rasa truncate ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                            {c.name || c.email}
                            {c.main && <span className="text-blue-500 text-[10px]"> (Primary)</span>}
                            {c.level === 'property' && <span className="text-[10px] opacity-60"> · property</span>}
                          </div>
                          <div className={`text-[11px] font-rasa truncate ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                            {c.email}{c.last_login ? ` · last login ${c.last_login}` : ' · no login'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {contacts.length === 0 && (!mirror?.contacts || mirror.contacts.length === 0) && (
                  <div className={`text-[12px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                    No contact on file for this property in the processor.
                  </div>
                )}
              </div>
            )}

            {activeSection === 'files' && (
              <div>
                {tenantFiles.length > 0 && (
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => {
                        // Quote, and neutralize CSV/Excel formula injection on leading =,+,-,@.
                        const cell = (c) => {
                          let s = String(c ?? '');
                          if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
                          return `"${s.replace(/"/g, '""')}"`;
                        };
                        const rows = [['Unit', 'Tenant', 'Building', 'Status'],
                          ...tenantFiles.map((tf) => [tf.unit, tf.tenant_name, tf.building, tf.status])];
                        const csv = `\uFEFF${rows.map((r) => r.map(cell).join(',')).join('\r\n')}`;
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
                        a.download = `${(property?.property_name || 'selected').replace(/\s+/g, '-')}-units.csv`;
                        a.click();
                        setTimeout(() => URL.revokeObjectURL(a.href), 1000);
                      }}
                      className="text-[12px] font-rasa text-blue-600 hover:underline"
                    >
                      Download CSV
                    </button>
                  </div>
                )}
                {tenantFiles.length === 0 ? (
                  <div className={`text-center py-8 text-[13px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>No tenant files yet</div>
                ) : (
                  <div className="space-y-1.5">
                    {tenantFiles.map((tf) => (
                      <div key={tf.id} className={`p-2.5 rounded-lg ${d ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between">
                          <div className={`text-[12px] font-rasa font-medium ${d ? 'text-gray-200' : 'text-gray-700'}`}>{tf.tenant_name}</div>
                          <Badge status={tf.status} />
                        </div>
                        <div className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                          {[tf.unit && `Unit ${tf.unit}`, tf.auditor, tf.file_received_date && `Rcvd ${tf.file_received_date}`].filter(Boolean).join(' · ')}
                        </div>
                        {tf.corrections?.length > 0 && (
                          <div className="mt-1 flex gap-1">
                            {tf.corrections.map((c) => (
                              <span key={c.round_number} className={`text-[10px] px-1.5 py-0.5 rounded font-rasa ${c.corrections_received_date ? (d ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500') : 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300'}`}>
                                R{c.round_number}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeSection === 'timeline' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add a note..."
                    className={`flex-1 px-3 py-1.5 rounded-lg border text-[12px] font-rasa ${d ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'} focus:outline-none focus:ring-1 focus:ring-blue-500`}
                    onKeyDown={(e) => e.key === 'Enter' && addNote()}
                  />
                  <button onClick={addNote} disabled={!note.trim()} className="px-3 py-1.5 rounded-lg text-[12px] font-rasa bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">Add</button>
                </div>

                {events.map((evt) => (
                  <div key={evt.id} className={`flex gap-2.5 p-2 rounded-lg ${d ? 'bg-gray-700/20' : 'bg-gray-50'}`}>
                    <div className={`mt-0.5 shrink-0 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                      {evt.event_type === 'status_change' ? <ArrowRight size={13} /> :
                       evt.event_type === 'note_added' ? <MessageSquare size={13} /> :
                       evt.event_type === 'email_sent' ? <Mail size={13} /> :
                       evt.event_type === 'assigned' ? <User size={13} /> :
                       <Clock size={13} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-[12px] font-rasa ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                        {evt.event_type === 'status_change' && <>Status: <Badge status={evt.from_status} /> → <Badge status={evt.to_status} /></>}
                        {evt.event_type === 'note_added' && (evt.details?.note || 'Note added')}
                        {evt.event_type === 'email_sent' && `Email sent: ${evt.details?.type?.replace(/_/g, ' ') || ''}`}
                        {evt.event_type === 'assigned' && `Assigned to ${evt.details?.assigned_to || '-'}`}
                        {evt.event_type === 'property_created' && 'Created'}
                        {evt.event_type === 'file_received' && 'Files received'}
                        {evt.event_type === 'import' && 'Data imported'}
                        {!['status_change','note_added','email_sent','assigned','property_created','file_received','import'].includes(evt.event_type) && evt.event_type?.replace(/_/g, ' ')}
                      </div>
                      <div className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                        {evt.performed_by?.username || 'System'} · {new Date(evt.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeSection === 'jpro' && property && (
              <AskJPro
                propertyData={{
                  ...property,
                  contacts,
                  tenant_files: tenantFiles,
                  events: events.slice(0, 10),
                }}
                mode="property"
              />
            )}
          </div>
        </>
      )}

      {/* Modals */}
      <AuditFileSelectionModal
        isOpen={showFileSelection}
        onClose={() => setShowFileSelection(false)}
        property={property}
        onSelectionComplete={(p) => { setProperty(p); fetchDetail(); onPropertyUpdated?.(); }}
      />
      <UnitSelectionModal
        isOpen={showUnitSelection}
        onClose={() => setShowUnitSelection(false)}
        property={property}
        onSelectionComplete={() => { fetchDetail(); onPropertyUpdated?.(); }}
      />
      <AuditManualEntryForm
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        auditId={property?.audit_id}
        editProperty={property}
        onSaved={() => { fetchDetail(); onPropertyUpdated?.(); }}
      />
    </div>
  );
};

export default PropertySidePanel;
